/** Each platform's product name, as its vendor writes it — for "Moved from Gizmo" and the ledger entry's memo. */
export const MIGRATION_PLATFORM_NAMES = {
    ggleap: 'ggLeap',
    gizmo: 'Gizmo',
    senet: 'Senet',
    icafecloud: 'iCafeCloud',
    pancafe: 'PanCafe Pro',
    smartlaunch: 'SmartLaunch',
    antamedia: 'Antamedia',
    handycafe: 'HandyCafe',
    truecafe: 'TrueCafe',
    other: 'the previous system',
};
/** The template each platform reads through. A platform adapter replaces its entry here. */
export const TEMPLATE_FOR_PLATFORM = {
    ggleap: 'clavian_csv',
    gizmo: 'clavian_csv',
    senet: 'clavian_csv',
    icafecloud: 'clavian_csv',
    pancafe: 'clavian_csv',
    smartlaunch: 'clavian_csv',
    antamedia: 'clavian_csv',
    handycafe: 'clavian_csv',
    truecafe: 'clavian_csv',
    other: 'clavian_csv',
};
export const CLAVIAN_CSV_COLUMNS = [
    { field: 'sourceId', column: 'source_id', required: false, aliases: ['sourceid', 'id', 'uuid', 'userid', 'memberid', 'accountid', 'customerid', 'userUuid'.toLowerCase()] },
    { field: 'username', column: 'username', required: true, aliases: ['username', 'user', 'login', 'account', 'nickname', 'membername', 'gamertag', 'handle'] },
    { field: 'firstName', column: 'first_name', required: false, aliases: ['firstname', 'givenname', 'name', 'fname'] },
    { field: 'lastName', column: 'last_name', required: false, aliases: ['lastname', 'surname', 'familyname', 'lname'] },
    { field: 'email', column: 'email', required: false, aliases: ['email', 'emailaddress', 'mail', 'emailid'] },
    { field: 'phone', column: 'phone', required: false, aliases: ['phone', 'mobile', 'mobilephone', 'phonenumber', 'telephone', 'mobileno', 'contact', 'contactno'] },
    { field: 'dateOfBirth', column: 'date_of_birth', required: false, aliases: ['dateofbirth', 'birthdate', 'birthday', 'dob'] },
    { field: 'joinedOn', column: 'joined_on', required: false, aliases: ['joinedon', 'joined', 'createdat', 'created', 'registeredat', 'registered', 'membersince', 'createdtime', 'signupdate'] },
    { field: 'lastVisitOn', column: 'last_visit_on', required: false, aliases: ['lastvisiton', 'lastvisit', 'lastseen', 'lastlogin', 'lastloginat', 'lastactive'] },
    { field: 'cashBalance', column: 'cash_balance', required: false, aliases: ['cashbalance', 'balance', 'moneybalance', 'deposit', 'deposits', 'wallet', 'walletbalance', 'credit', 'amount'] },
    { field: 'bonusBalance', column: 'bonus_balance', required: false, aliases: ['bonusbalance', 'bonus', 'promobalance', 'freebalance'] },
    { field: 'timeBalance', column: 'time_balance', required: false, aliases: ['timebalance', 'time', 'timeleft', 'remainingtime', 'playtime', 'seconds', 'minutes', 'timeleftseconds', 'prepaidtime'] },
    { field: 'timePcGroups', column: 'time_pc_groups', required: false, aliases: ['timepcgroups', 'pcgroup', 'pcgroups', 'hostgroup', 'hostgroups', 'zone', 'zones', 'machinegroup'] },
    { field: 'currency', column: 'currency', required: false, aliases: ['currency', 'currencycode'] },
    { field: 'banned', column: 'banned', required: false, aliases: ['banned', 'isbanned', 'locked', 'blocked', 'isblocked'] },
    { field: 'banReason', column: 'ban_reason', required: false, aliases: ['banreason', 'blockreason', 'lockreason'] },
    { field: 'note', column: 'note', required: false, aliases: ['note', 'notes', 'comment', 'comments', 'memo', 'remarks'] },
    { field: 'deleted', column: 'deleted', required: false, aliases: ['deleted', 'isdeleted', 'disabled', 'isdisabled', 'inactive'] },
];
/** The column names of the Clavian CSV, in order. */
export const CLAVIAN_CSV_HEADER = CLAVIAN_CSV_COLUMNS.map((spec) => spec.column);
/**
 * A downloadable sample: the header and three made-up players — one with an email, one phone-only with prepaid time on
 * two PC groups, one under 18. Money in rupees with paise, time as `h:mm`, dates `YYYY-MM-DD`.
 */
export function clavianCsvSample() {
    return [
        [...CLAVIAN_CSV_HEADER],
        ['1001', 'ravi_k', 'Ravi', 'Kumar', 'ravi.kumar@example.com', '+91 98450 12345', '1998-04-12', '2021-06-01', '2026-10-01', '450.00', '50.00', '2:00', 'Regular', 'INR', 'no', '', 'Prefers PC 07', 'no'],
        ['1002', 'aisha07', 'Aisha', 'Khan', '', '98450 67890', '2001-11-30', '2022-01-15', '2026-09-28', '1,250.50', '0', '5:30', 'VIP;Regular', 'INR', 'no', '', '', 'no'],
        ['1003', 'neo_15', 'Neo', 'Das', 'neo.das@example.com', '', '2011-02-03', '2025-05-20', '2026-10-05', '120', '', '', '', 'INR', 'no', '', 'Comes with his father', 'no'],
    ];
}
/** Lower-case, and drop spaces, underscores, hyphens and dots: `Email Address`, `email_address` → `emailaddress`. */
export function normaliseHeader(header) {
    return header.trim().toLowerCase().replace(/[\s_.\-()[\]]+/g, '');
}
/**
 * Suggest which source column feeds each field, from the file's headers: an exact Clavian CSV column name first, then
 * the aliases, never one column for two fields. HQ confirms or changes it on the mapping step.
 */
export function suggestColumnMapping(headers) {
    const normalised = headers.map(normaliseHeader);
    const taken = new Set();
    const mapping = {};
    // Exact Clavian column names win, then aliases in field order.
    for (const spec of CLAVIAN_CSV_COLUMNS) {
        const index = normalised.indexOf(normaliseHeader(spec.column));
        if (index >= 0 && !taken.has(index)) {
            mapping[spec.field] = index;
            taken.add(index);
        }
    }
    for (const spec of CLAVIAN_CSV_COLUMNS) {
        if (mapping[spec.field] !== undefined)
            continue;
        for (const alias of spec.aliases) {
            const index = normalised.findIndex((header, position) => header === alias && !taken.has(position));
            if (index >= 0) {
                mapping[spec.field] = index;
                taken.add(index);
                break;
            }
        }
    }
    return mapping;
}
//# sourceMappingURL=template.js.map